import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import api from '../services/api';
import './TaskNotificationBell.css';

const TaskNotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [assignedCount, setAssignedCount] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [recentConversations, setRecentConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await api.get('/calendar/tasks');
        setTasks(response.data);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };
    fetchTasks();
  }, []);

  // Fetch unread messages and conversations
  useEffect(() => {
    const fetchMessagesData = async () => {
      try {
        // Fetch unread count
        const unreadResponse = await api.get('/messenger/unread-count');
        setUnreadMessages(unreadResponse.data.unread_count || 0);

        // Fetch recent conversations with unread messages
        const convosResponse = await api.get('/messenger/conversations');
        const convosWithUnread = convosResponse.data.filter(c => c.unread_count > 0).slice(0, 5);
        setRecentConversations(convosWithUnread);
      } catch (error) {
        console.error('Error fetching messages data:', error);
      }
    };

    if (user?.role === 'admin') {
      fetchMessagesData();
      // Poll for new messages every 30 seconds
      const interval = setInterval(fetchMessagesData, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Calculate assigned tasks for current user
  useEffect(() => {
    const userAssignedTasks = tasks.filter(
      task => task.assigned_to === user?.id && task.status !== 'completed'
    );
    setAssignedCount(userAssignedTasks.length);
  }, [tasks, user]);

  // Only show for admin users
  if (user?.role !== 'admin') {
    return null;
  }

  const userAssignedTasks = tasks
    .filter(task => task.assigned_to === user?.id && task.status !== 'completed')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5); // Show top 5 tasks

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      const response = await api.put(
        `/calendar/tasks/${taskId}`,
        { status: newStatus }
      );

      const updatedTask = response.data;
      setTasks(tasks.map(task =>
        task.id === taskId ? updatedTask : task
      ));

      // Update selectedTask if it's currently displayed
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status');
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
    setShowDropdown(false);
  };

  return (
    <div className="task-notification-container">
      <button
        className="notification-bell"
        onClick={() => setShowDropdown(!showDropdown)}
        title="View assigned tasks"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {(assignedCount + unreadMessages) > 0 && (
          <span className="notification-badge">{assignedCount + unreadMessages}</span>
        )}
      </button>

      {showDropdown && (
        <div className="task-dropdown">
          <div className="dropdown-tabs">
            <button
              className={`dropdown-tab ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveTab('tasks')}
            >
              Tasks {assignedCount > 0 && <span className="tab-badge">{assignedCount}</span>}
            </button>
            <button
              className={`dropdown-tab ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              Messages {unreadMessages > 0 && <span className="tab-badge">{unreadMessages}</span>}
            </button>
          </div>

          {activeTab === 'tasks' && (
            <>
              <div className="dropdown-content">
                {userAssignedTasks.length === 0 ? (
                  <p className="no-tasks">No assigned tasks</p>
                ) : (
                  <div className="tasks-list">
                    {userAssignedTasks.map((task) => (
                      <div
                        key={task.id}
                        className="dropdown-task"
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="task-info">
                          <h4>{task.title}</h4>
                          <p className="task-date">
                            {new Date(task.task_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div
                          className="task-arrow"
                          onClick={(e) => e.stopPropagation()}
                        >
                          →
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {assignedCount > 5 && (
                <div className="dropdown-footer">
                  <p>{assignedCount - 5} more tasks...</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'messages' && (
            <>
              <div className="dropdown-content">
                {recentConversations.length === 0 ? (
                  <p className="no-tasks">No unread messages</p>
                ) : (
                  <div className="tasks-list">
                    {recentConversations.map((convo) => {
                      const otherParticipant = convo.participants?.find(p => p.id !== user?.id);
                      const displayName = convo.is_group
                        ? convo.name
                        : (otherParticipant?.display_name || 'Unknown');
                      return (
                        <div
                          key={convo.id}
                          className="dropdown-task message-item"
                          onClick={() => {
                            navigate('/dashboard?tab=messenger&conversation=' + convo.id);
                            setShowDropdown(false);
                          }}
                        >
                          <div className="message-avatar">
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                          <div className="task-info">
                            <h4>{displayName}</h4>
                            <p className="task-date">
                              {convo.last_message?.content?.substring(0, 30)}
                              {convo.last_message?.content?.length > 30 ? '...' : ''}
                            </p>
                          </div>
                          <span className="unread-badge">{convo.unread_count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="dropdown-footer clickable" onClick={() => {
                navigate('/dashboard?tab=messenger');
                setShowDropdown(false);
              }}>
                <p>View all messages</p>
              </div>
            </>
          )}
        </div>
      )}

      {showDropdown && (
        <div
          className="dropdown-overlay"
          onClick={() => setShowDropdown(false)}
        />
      )}

      {/* Task Detail Modal */}
      {showTaskDetail && selectedTask && (
        <div className="task-detail-overlay" onClick={() => setShowTaskDetail(false)}>
          <div className="task-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-detail-header">
              <h3>{selectedTask.title}</h3>
              <button
                className="task-detail-close"
                onClick={() => setShowTaskDetail(false)}
              >
                ✕
              </button>
            </div>

            <div className="task-detail-body">
              {selectedTask.description && (
                <div className="task-section">
                  <h4>Description</h4>
                  <p>{selectedTask.description}</p>
                </div>
              )}

              <div className="task-section">
                <h4>Task Details</h4>
                <div className="task-details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Date</span>
                    <span className="detail-value">
                      {new Date(selectedTask.task_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Priority</span>
                    <span className={`detail-value priority-${selectedTask.priority}`}>
                      {selectedTask.priority}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created By</span>
                    <span className="detail-value">
                      {selectedTask.created_by_name || 'Unknown'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Assigned To</span>
                    <span className="detail-value">
                      {selectedTask.assigned_to_name || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="task-section">
                <h4>Status</h4>
                <select
                  value={selectedTask.status}
                  onChange={(e) => handleTaskStatusChange(selectedTask.id, e.target.value)}
                  className={`task-status-select status-${selectedTask.status}`}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskNotificationBell;
